import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MessageInboxItem, MessageItemDetails, MessageOutboxItem, MeyMessageInboxClient } from '../../../web-api-client';
import { Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-read-outbox',
  templateUrl: './read-outbox.component.html',
  styleUrls: ['./read-outbox.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class ReadOutBoxComponent implements OnInit {

  public onClose: Subject<void> = new Subject();
  public message: MessageOutboxItem;

  public messageDetails: MessageItemDetails;
  public haveAttachments = true;
  public loading = true;
  public drawerOpen = false;

  constructor(
    public client: MeyMessageInboxClient,
    public changeDetecRef: ChangeDetectorRef,
    public activeModal: NgbActiveModal
  ) {

  }

  ngOnInit(): void {
    this.client.getMessageDetails(this.message.messageId).subscribe(item => {
      this.messageDetails = item;
      this.loading = false;
      this.changeDetecRef.detectChanges();
    });
  }

  closeModal(): void {
    this.onClose.next();
    this.activeModal.close();
  }

  openFile(url: string): void {
    window.open(url, '_blank');
  }

  formatDate(date: any): string {
    if (!date) return 'Няма дата';

    const d = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - d.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    if (diffDays === 0) {
      return 'Днес, ' + d.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Вчера, ' + d.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' });
    } else {
      return d.toLocaleDateString('bg-BG', options);
    }
  }

  hasFields(): boolean {
    return this.messageDetails?.fields && Object.keys(this.messageDetails.fields).length > 0;
  }

  getFieldsArray(): Array<{key: string, value: any}> {
    if (!this.messageDetails?.fields) return [];

    return Object.keys(this.messageDetails.fields).map(key => ({
      key: key,
      value: this.formatFieldValue(this.messageDetails.fields[key])
    }));
  }

  formatFieldValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return 'N/A';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(2);

    return `${size} ${sizes[i]}`;
  }

  toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    this.changeDetecRef.detectChanges();
  }

  getParsedFields(): any[] {
    if (!this.messageDetails?.fields) return [];

    const parsedFields: any[] = [];

    Object.keys(this.messageDetails.fields).forEach(key => {
      const value = this.messageDetails.fields[key];

      // Try to parse JSON strings
      if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
        try {
          const parsed = JSON.parse(value);
          // Skip attachment arrays - they'll be shown in the attachments section
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].blobId) {
            return; // Skip this field
          }
          parsedFields.push({
            key: key,
            value: parsed,
            isArray: Array.isArray(parsed),
            isParsed: true
          });
        } catch (e) {
          parsedFields.push({
            key: key,
            value: value,
            isArray: false,
            isParsed: false
          });
        }
      } else {
        parsedFields.push({
          key: key,
          value: value,
          isArray: false,
          isParsed: false
        });
      }
    });

    return parsedFields;
  }

  getEnhancedAttachments(): any[] {
    if (!this.messageDetails?.attachments) return [];

    // Try to find detailed attachment info from fields
    let detailedAttachments: any[] = [];

    if (this.messageDetails.fields) {
      Object.keys(this.messageDetails.fields).forEach(key => {
        const value = this.messageDetails.fields[key];

        // Try to parse JSON strings that might contain attachment details
        if (typeof value === 'string' && value.startsWith('[')) {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].blobId) {
              detailedAttachments = parsed;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      });
    }

    // If we have detailed attachments, return them; otherwise return basic attachments
    return detailedAttachments.length > 0 ? detailedAttachments : this.messageDetails.attachments;
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('bg-BG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  getRawFieldsJson(): string {
    if (!this.messageDetails?.fields) return '{}';
    return JSON.stringify(this.messageDetails.fields, null, 2);
  }

}
